import {
  Stack,
  Avatar,
  useMediaQuery, Divider
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { Users } from "lucide-react";
import {
  useState
} from "react";
import { useTranslation } from "react-i18next";

import { SharedItemCard } from "./SharedItemCard.component";

import type { TopicCardProps } from "./TopicCardShare.types";

import {
  CtaCommunityOverlay,
  ModifyContentModal,
  CommentsSection
} from "..";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { CommonWidgetContainer } from "../../components/Commons/CommonWidgetContainer.styles";
import { DeleteContentModal } from "../../components/DeleteContentModal";
import {
  BodyTypography,
  OwnerNameTypography,
  TopicCardTop
} from "../../components/TopicCard/TopicCard.styles";
import { TopicCardSignature } from "../../components/TopicCardSignature";
import { POST_TYPE, ROLES } from "../../consts";
import { useAuthStore } from "../../stores";
import { TypographyTextTag } from "../../styles/Typography/TypographyTextTag.styles";
import { stringAvatar, useGetCreatedLabel } from "../../utils/community";
import { BadgeBox } from "../Commons/BadgeBox";

export function TopicCardShare(props: TopicCardProps) {
  const {
    body,
    changed,
    commentsNumber,
    created,
    id,
    learningObjectTypology,
    likes,
    ownerImage,
    ownerName,
    sharedLo,
    selectedTopic,
    selectedFilter,
    topicOwner
  } = props;
  const theme = useTheme();
  const { t } = useTranslation();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isModifyOpen, setIsModifyOpen] = useState(false);
  const[showComments,setShowComments]=useState(false);

  const userData = useAuthStore(state => state.userData);

  const isModerator = userData?.roles.some(role => role === ROLES.BUSINESS_ADMIN || role === ROLES.PRODUCT_OWNER) ;
  const userId = useAuthStore(state => state.userData?.id_user);

  const isEditable =  (userId && topicOwner == userId.toString());
  const isModified = created !== changed;

  return (
    <CommonWidgetContainer
      sx={ {
        width:"100% !important",
        maxWidth:"100%" ,
        borderRadius: "1rem",
        padding: "1.5rem",
        "& p": {
          color: theme.customColors.exploreCardText
        },
        "& span": {
          color: theme.customColors.exploreCardText
        },
        "& .icon path": {
          fill: theme.customColors.exploreCardText + "!important"
        }
      } }
      hasSidePadding={ true }
      hoverBorderAccent={ false }
    >
      <Stack
        sx={ {
          position: "relative"
        } }
        gap={ "24px" }
      >
        <TopicCardTop
          tabIndex={ -1 }
          direction={ "row" }
          gap={ "16px" }
          alignItems={ "center" }
        >
          <Avatar
            aria-hidden={ true }
            alt={ `${t("avatar_user")} ${ownerName}` }
            { ...stringAvatar(ownerName) }
            src={ ownerImage }
            sx={ {
              height: isMobile ? "32px" : "56px",
              width: isMobile ? "32px" : "56px",
              alignSelf: "start"
            } }
          />
          <Stack
            gap={ 1 }
            flexGrow={ 1 }
          >
            <Stack
              aria-hidden={ true }
              direction={ "row" }
              justifyContent={ "space-between" }
              alignItems={ "start" }
              gap={ "1rem" }
            >
              <Stack
                gap={ 0.5 }
              >
                <OwnerNameTypography variant="h4">
                  { ownerName }
                </OwnerNameTypography>
                <TopicCardSignature
                  created={ useGetCreatedLabel(created) }
                  isModified={ isModified }
                  size={ "medium" }
                />
              </Stack>
              <Stack
                alignItems={ "end" }
              >
                <BadgeBox
                  background={ theme.customColors.backgroundSecondary }
                  textColor={ "inherit" }
                  border={ `1px solid ${theme.customColors.textTag}` }>
                  <Stack
                    direction={ "row" }
                    alignItems={ "center" }
                    gap={ "0.44rem" }
                  >
                    <Users size={ 16 } color={ theme.customColors.textTag }/>
                    <TypographyTextTag>
                      { t("share") }
                    </TypographyTextTag>
                  </Stack>
                </BadgeBox>
                {
                  isEditable || isModerator ?
                    <Stack
                      direction="row"
                      alignItems={ "flex-end" }
                      justifyContent={ "end" }
                      gap={ "0.5rem" }
                    >
                      {
                        isEditable ?
                          <ButtonDigited
                            aria-label={ `${t("modify")} post` }
                            typology="tertiary"
                            sx={ {
                              fontSize: "0.75rem"
                            } }
                            onClick={ () => setIsModifyOpen(true) }
                          >
                            { t("modify") }
                          </ButtonDigited>  : null
                      }

                      <ButtonDigited
                        typology="tertiary"
                        aria-label={ `${t("delete")} post` }
                        sx={ {
                          paddingRight:"0",
                          fontSize: "0.75rem"
                        } }
                        onClick={ () => setIsDeleteOpen(true) }
                      >
                        { t("delete") }
                      </ButtonDigited>
                    </Stack>  :
                    null
                }
              </Stack>
            </Stack>
          </Stack>
        </TopicCardTop>

        <BodyTypography >
          { body }
        </BodyTypography>

        {
          sharedLo && learningObjectTypology ?
            <>
              <Divider/>
              <SharedItemCard
                title={ sharedLo.title }
                shortDescription={ sharedLo.description }
                id={ Number(sharedLo.lo_id) }
                learningObjectTypology={ learningObjectTypology }
                coverPublicURL={ sharedLo.publicCoverUrl }
                topicTags={ sharedLo.topics }
              />
              <Divider/>
            </>:
            null
        }


        <CtaCommunityOverlay
          id={ id }
          comments={ commentsNumber }
          likes={ likes }
          commentsOpened={ showComments }
          commentsClickAction={ () => {
            setShowComments(!showComments);
          } }
          selectedFilter={ selectedFilter }
          selectedTopic={ selectedTopic }
        />

        {
          isDeleteOpen && sharedLo ?

            <DeleteContentModal
              closeDrawer={ ()=> {
                setIsDeleteOpen(false);
              } }
              id={ id }
              isModerator={ Boolean(isModerator) }
              loId={ sharedLo.lo_id }
              onClose={ ()=> {
                setIsDeleteOpen(false);
              } }
              open={ isDeleteOpen }
              postType={ POST_TYPE.SHARED }
              selectedTopic={ selectedTopic }
              selectedFilter={ selectedFilter }
            /> : null
        }

        {
          isModifyOpen && sharedLo?
            <ModifyContentModal
              selectedFilter={ selectedFilter }
              selectedTopic={ selectedTopic }
              open={ isModifyOpen }
              onClose={ () => setIsModifyOpen(false) }
              id={ id }
              loId={ sharedLo.lo_id }
              postType={ POST_TYPE.SHARED }
              body={ body }
              closeDrawer={ () => setIsModifyOpen(false) }
            />
            : null

        }

      </Stack>

      <CommentsSection
        topicOwnerId={ topicOwner }
        selectedFilter={ selectedFilter }
        selectedTopic={ selectedTopic }
        discussionId={ id }
        enabled={ showComments }
        className={ showComments ? "visible" : "" } />

    </CommonWidgetContainer>
  );
}
